/**
 * HANNE BLOCH gift pop-up ("A GIFT FOR YOU"), rebuilt October 2026.
 *
 * Reads its markup from snippets/gift-floating-button.liquid (#hb-gift carries the settings).
 *
 * Link flows (the code is applied here through /cart/update.js, so the parameters survive):
 *   ?gift=GIFT-XXXXX                 shop flow: pick a bag, ADD TO MY ORDER, pop-up closes, stay on the page
 *   ?gift=GIFT-XXXXX&flow=checkout   checkout flow: pick a bag, ADD GIFT & GO TO CHECKOUT (or Continue
 *                                    shopping, which adds the bag and closes)
 *   ?gift=open                       just opens the pop-up (no code applied); ?drawstring=open is an alias
 * A gift code already on the cart (arrival through /discount/CODE?redirect=) opens the shop flow once
 * per session while no bag has been chosen. The flow is remembered for the session so the floating tab
 * and "Change gift" in the cart drawer reopen the same pop-up.
 *
 * states: gift (a code starting with the prefix is on the cart), purchasable (theme setting, bags at
 * their price), hidden (nothing renders). Nothing in here adds or removes a bag on its own.
 */
import { CartUpdateEvent } from '@theme/events';

const root = document.getElementById('hb-gift');
const panel = document.getElementById('freeGiftToast');

if (root && panel) {
  const state = {
    mode: 'hidden', // 'gift' | 'purchasable' | 'hidden', resolved from /cart.js
    flow: 'shop', // 'shop' | 'checkout'
    nocodeMode: root.dataset.nocodeMode || 'hidden',
    prefix: (root.dataset.prefix || 'GIFT-').toUpperCase(),
    checkoutUrl: root.dataset.checkoutUrl || '/checkout',
    shipThresholdCents: Number(root.dataset.shipThresholdCents || 0),
    selected: null, // variant id of the bag shown on the stage
    busy: false,
    lastFocus: null,
    cartChanged: false // a bag was added while open; the cart drawer is told on close
  };

  const els = {
    status: panel.querySelector('[data-gift-status]'),
    primary: panel.querySelector('[data-gift-primary]'),
    primaryLabel: panel.querySelector('[data-gift-primary-label]'),
    secondary: panel.querySelector('[data-gift-secondary]'),
    close: panel.querySelectorAll('.popup__close_threshold'),
    tiles: Array.from(panel.querySelectorAll('[data-gift-tile]')),
    stageItems: Array.from(panel.querySelectorAll('[data-gift-stage-item]')),
    name: panel.querySelector('[data-gift-name]'),
    priceWrap: panel.querySelector('[data-gift-price-wrap]'),
    price: panel.querySelector('[data-gift-price]'),
    priceLabel: panel.querySelector('[data-gift-price-label]'),
    shipUnder: panel.querySelector('[data-ship-copy="under"]'),
    shipOver: panel.querySelector('[data-ship-copy="over"]'),
    tab: document.querySelector('[data-gift-tab]'),
    copies: panel.querySelectorAll('[data-gift-copy]')
  };

  const LABELS = {
    shop: 'Add gift to my order',
    checkout: 'Add gift & go to checkout',
    purchasable: 'Add to my order',
    close: 'Continue shopping'
  };

  // Option B (Vic, 2026-10-08): the GIFT code only applies once a paid piece is in the cart.
  const PRICE_LABEL = 'Complimentary';
  const PRICE_LABEL_FIRST = 'Complimentary with your first piece';

  /* ---------- session memory ---------- */

  function remember(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable: the pop-up just forgets between pages */
    }
  }

  function recall(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /* ---------- cart helpers ---------- */

  async function cartRequest(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.description || data?.message || `${path} failed`);
    return data;
  }

  async function getCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) throw new Error('cart fetch failed');
      return await res.json();
    } catch (err) {
      console.error('[HB gift] cart fetch error', err);
      return null;
    }
  }

  function findGiftCode(cart) {
    const codes = cart?.discount_codes || [];
    return codes.find((dc) => String(dc.code || '').toUpperCase().startsWith(state.prefix)) || null;
  }

  function isBagLine(item) {
    return item?.properties?._free_gift === 'Yes';
  }

  // What the customer actually pays for, bag excluded: decides the shipping line.
  function paidSubtotalCents(cart) {
    return (cart?.items || [])
      .filter((item) => !isBagLine(item))
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
  }

  // A paid piece: any line that is not the chosen bag and not one of the mini bags themselves
  // (a bag bought alongside does not make the GIFT code apply).
  const bagProductIds = new Set(els.tiles.map((t) => Number(t.dataset.productId)).filter(Boolean));
  function hasPaidPiece(cart) {
    return (cart?.items || []).some((item) => !isBagLine(item) && !bagProductIds.has(Number(item.product_id)));
  }

  function updatePriceLabel(cart) {
    if (!els.priceLabel || !cart) return; // no cart read: keep what the page rendered
    els.priceLabel.textContent = hasPaidPiece(cart) ? PRICE_LABEL : PRICE_LABEL_FIRST;
  }

  function updateShipping(cart) {
    if (!els.shipUnder || !els.shipOver || !state.shipThresholdCents) return;
    const over = paidSubtotalCents(cart) >= state.shipThresholdCents;
    els.shipUnder.hidden = over;
    els.shipOver.hidden = !over;
  }

  async function applyCode(code) {
    try {
      await cartRequest('/cart/update.js', { discount: code });
    } catch (err) {
      console.error('[HB gift] could not apply the gift code', err);
    }
  }

  /* ---------- UI helpers ---------- */

  function setStatus(text) {
    if (!els.status) return;
    els.status.textContent = text || '';
    els.status.hidden = !text;
  }

  function setPrimary(label, handler) {
    if (!els.primary) return;
    els.primaryLabel.textContent = label;
    els.primary.hidden = false;
    els.primary.onclick = handler;
  }

  function setSecondary(visible) {
    if (!els.secondary) return;
    els.secondary.hidden = !visible;
  }

  function setBusy(flag) {
    state.busy = flag;
    panel.classList.toggle('is-busy', flag);
    if (els.primary) els.primary.disabled = flag;
  }

  function select(variantId) {
    state.selected = String(variantId);
    els.tiles.forEach((tile) => {
      const on = tile.dataset.variantId === state.selected;
      tile.classList.toggle('is-selected', on);
      tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on && els.name) els.name.textContent = tile.dataset.productTitle || '';
      if (on && els.price) els.price.textContent = tile.dataset.price || '';
    });
    els.stageItems.forEach((item) => {
      item.hidden = item.dataset.variantId !== state.selected;
    });
  }

  function selectDefault(cart) {
    // The bag already in the cart wins, else the first bag that can be added.
    const inCart = (cart?.items || []).find(isBagLine);
    const fromCart = inCart && els.tiles.find((t) => Number(t.dataset.variantId) === Number(inCart.variant_id) && !t.disabled);
    const first = els.tiles.find((t) => !t.disabled);
    const pick = fromCart || first;
    if (pick) select(pick.dataset.variantId);
  }

  function applyFlowUI() {
    const gift = state.mode === 'gift';
    const checkoutFlow = gift && state.flow === 'checkout';
    setPrimary(checkoutFlow ? LABELS.checkout : gift ? LABELS.shop : LABELS.purchasable, onPrimary);
    setSecondary(checkoutFlow);
    setStatus('');
  }

  /* ---------- open / close ---------- */

  // The theme's cart drawer is a <dialog> in the browser's top layer; anything opened while it is
  // up renders underneath it, so any open drawer is closed before the pop-up shows. (A top-layer
  // <dialog> for the pop-up itself was tried and dropped: the bag images did not paint inside it.)
  function closeOtherDialogs() {
    document.querySelectorAll('dialog[open]').forEach((dlg) => {
      if (dlg === panel) return;
      const host = dlg.closest('cart-drawer-component');
      if (host && typeof host.close === 'function') host.close();
      else if (typeof dlg.close === 'function') dlg.close();
      // The theme closes its drawer after a slide-out animation. If that animation never ends
      // (background tab, animations off), close the dialog ourselves so it cannot sit on top.
      setTimeout(() => {
        if (!dlg.open) return;
        dlg.classList.remove('dialog-closing');
        if (typeof dlg.close === 'function') dlg.close();
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }, 700);
    });
  }

  function openPopup() {
    if (panel.classList.contains('is-visible')) return;
    state.lastFocus = document.activeElement;
    closeOtherDialogs();
    // The bag images are lazy so a hidden pop-up costs nothing; once it opens, fetch all of them
    // (the hidden stage items too) so switching swatches never shows an empty stage.
    panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    applyFlowUI();
    getCart().then((cart) => {
      updateShipping(cart);
      updatePriceLabel(cart);
    });
    panel.classList.add('is-visible');
    document.body.classList.add('hb-gift-open');
    const first = panel.querySelector('.hb-gift-swatch.is-selected') || panel.querySelector('.popup__close_threshold');
    first?.focus({ preventScroll: true });
  }

  function closePopup() {
    panel.classList.remove('is-visible');
    document.body.classList.remove('hb-gift-open');
    setStatus('');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus({ preventScroll: true });
    if (state.cartChanged) {
      state.cartChanged = false;
      document.dispatchEvent(new CartUpdateEvent({ source: 'hb-gift-popup' }));
    }
  }

  /* ---------- actions ---------- */

  async function addBag(variantId) {
    const payload = { id: Number(variantId), quantity: 1 };
    if (state.mode === 'gift') {
      payload.properties = { _free_gift: 'Yes' };
      // One bag per order: swap an existing bag line for the new choice.
      const cart = await getCart();
      const existing = (cart?.items || []).find(isBagLine);
      if (existing && Number(existing.variant_id) === Number(variantId)) return cart;
      if (existing) await cartRequest('/cart/change.js', { id: existing.key, quantity: 0 });
    }
    return cartRequest('/cart/add.js', payload);
  }

  async function addSelected(goToCheckout) {
    if (state.busy || !state.selected) return;
    setStatus('');
    setBusy(true);
    try {
      await addBag(state.selected);
      state.cartChanged = true;

      if (!goToCheckout) {
        closePopup();
        return;
      }

      const cart = await getCart();
      updateShipping(cart);
      updatePriceLabel(cart);
      const code = findGiftCode(cart);
      if (code && code.applicable) {
        setStatus('Taking you to checkout.');
        window.location.assign(state.checkoutUrl);
        return;
      }
      // The bag is in the cart but the code does not apply yet (nothing else in the cart, or the
      // code is used up). No amounts here: the discount settings are a back-office matter.
      setStatus(
        hasPaidPiece(cart)
          ? 'Your bag is in your cart. Add your pieces and it is complimentary at checkout.'
          : 'Your mini bag is in your cart. It is complimentary with your first piece.'
      );
      setPrimary(LABELS.close, closePopup);
      setSecondary(false);
    } catch (err) {
      console.error('[HB gift] add error', err);
      setStatus('Something went wrong adding the bag. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function onPrimary(e) {
    e?.preventDefault?.();
    addSelected(state.mode === 'gift' && state.flow === 'checkout');
  }

  /* ---------- state ---------- */

  function applyMode(mode) {
    state.mode = mode;
    root.dataset.state = mode;
    els.copies.forEach((el) => {
      el.hidden = el.dataset.giftCopy !== mode;
    });
    if (els.tab) els.tab.hidden = mode === 'hidden';
    // Gift state: the market price struck through beside "Complimentary" (option F).
    // Purchasable state: the plain price.
    if (els.priceWrap) els.priceWrap.classList.toggle('is-gift', mode === 'gift');
  }

  async function resolveMode() {
    const cart = await getCart();
    const code = findGiftCode(cart);
    if (code) return { mode: 'gift', cart };
    if (state.nocodeMode === 'purchasable') return { mode: 'purchasable', cart };
    return { mode: 'hidden', cart };
  }

  /* ---------- wiring ---------- */

  els.tiles.forEach((tile) =>
    tile.addEventListener('click', () => {
      if (tile.disabled || state.busy) return;
      select(tile.dataset.variantId);
      setStatus('');
    })
  );
  els.close.forEach((el) =>
    el.addEventListener('click', (e) => {
      e.preventDefault();
      closePopup();
    })
  );
  if (els.secondary) {
    els.secondary.addEventListener('click', (e) => {
      e.preventDefault();
      addSelected(false);
    });
  }
  panel.addEventListener('click', (e) => {
    if (e.target === panel) closePopup();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel.classList.contains('is-visible')) closePopup();
  });
  // Keep the price line current while the page is open (a piece added or removed in the cart), so the
  // pop-up never opens with yesterday's wording.
  document.addEventListener(CartUpdateEvent.eventName || 'cart:update', (e) => {
    if (state.mode !== 'gift' || e.detail?.resource?.source === 'hb-gift-popup') return; // our own close: already current
    getCart().then(updatePriceLabel);
  });
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-gift-open], .gift-floating-btn, .change-gift');
    if (!trigger) return;
    // Always swallow the click: "Change gift" sits inside the cart form and must never submit it.
    e.preventDefault();
    if (state.mode === 'hidden') return;
    openPopup();
  });

  /* ---------- boot ---------- */

  (async () => {
    const url = new URL(window.location.href);
    const giftParam = (url.searchParams.get('gift') || '').trim();
    const flowParam = (url.searchParams.get('flow') || '').trim().toLowerCase();
    const legacyOpen = url.searchParams.get('drawstring') === 'open';
    let forced = false;

    if (giftParam && giftParam.toLowerCase() !== 'open') {
      await applyCode(giftParam);
      state.flow = flowParam === 'checkout' ? 'checkout' : 'shop';
      remember('hb_gift_flow', state.flow);
      forced = true;
    } else if (giftParam.toLowerCase() === 'open' || legacyOpen) {
      forced = true;
    }

    if (!giftParam && !flowParam) {
      state.flow = recall('hb_gift_flow') === 'checkout' ? 'checkout' : 'shop';
    }

    // Clean the parameters off the address bar so a reload or a share does not replay them.
    if (giftParam || flowParam || legacyOpen) {
      ['gift', 'flow', 'drawstring'].forEach((k) => url.searchParams.delete(k));
      try {
        window.history.replaceState(window.history.state, '', url.pathname + (url.search || '') + url.hash);
      } catch (e) {
        /* ignore */
      }
    }

    const { mode, cart } = await resolveMode();
    applyMode(mode);
    selectDefault(cart);
    updateShipping(cart);
    updatePriceLabel(cart);
    if (mode === 'hidden') return;

    const hasBag = (cart?.items || []).some(isBagLine);
    const auto = mode === 'gift' && !hasBag && recall('hb_gift_seen') !== '1';
    if (forced || auto) {
      remember('hb_gift_seen', '1');
      openPopup();
    }
  })();
}
